import React, { useState, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useWindowWidth } from "../utils/useWidth";
import { useInView } from "framer-motion";

// eslint-disable-next-line no-unused-vars
import Booking from "../assets/img/booking-logo-blue.png";

const reviews = [
  {
    user: { name: "Camilla", date: "Maggio 2026", structure: "New Moon Cavour", score: 10 },
    text: "Stanza situata vicino al centro di La Spezia. Molto curata, bellissimo il lucernaio che rende la stanza luminosa, bagno e doccia davvero belli e confortevoli. Facile entrare nella struttura con le indicazioni fornite dal proprietario.",
  },
  {
    user: { name: "Luca", date: "Maggio 2026", structure: "New Moon Cavour", score: 9 },
    text: "Struttura e camere nuove e di gran gusto, curate nei minimi dettagli. Posizione strategica a pochi minuti a piedi dal centro e dalla stazione. Comodo il self check-in.",
  },
  {
    user: { name: "Abate", date: "Settembre 2023", structure: "New Moon Cavour", score: 10 },
    text: "La struttura magnifica, il gestore veramente una bravissima persona che è sempre disponibile per tutto e poi la camera merita tantissimo. Ci tornerò sicuramente.",
  },
  {
    user: { name: "Fabio", date: "Maggio 2025", structure: "New Moon Del Prione", score: 10 },
    text: "Pulizia, posizione centrale, silenzio, confortevole, accesso facile. Perfetto per vivere la città, vicinissimo alla stazione e a tutte le attrazioni. Perfetto per viaggiare tra le 5 Terre.",
  },
  {
    user: { name: "Camilla", date: "Maggio 2026", structure: "New Moon Del Prione", score: 10 },
    text: "Posizione strategica, in pieno centro, vicinissima alla stazione per il treno Cinque Terre Express. Camera super carina, non manca davvero nulla. Il proprietario è davvero gentile, ti spiega tutto in modo chiaro per il check-in. Davvero consigliato!",
  },
  {
    user: { name: "Guerra", date: "Giugno 2025", structure: "New Moon Del Prione", score: 10 },
    text: "Tutto! Ambiente, gusto dell'arredo, accessori, ordine, pulizia. Ho visitato bei posti, ci ritornerò sicuramente, compresa la struttura!",
  },
];

const HalfStar = () => (
  <span className="relative inline-block text-xs leading-none">
    <span className="text-yellow-400/30">★</span>
    <span className="absolute inset-0 overflow-hidden" style={{ width: "50%" }}>
      <span className="text-yellow-400">★</span>
    </span>
  </span>
);

const Stars = ({ score }) => {
  const fullStars = Math.floor(score / 2);
  const hasHalf = score % 2 !== 0;
  const emptyStars = 5 - fullStars - (hasHalf ? 1 : 0);
  return (
    <div className="flex items-center gap-px">
      {Array.from({ length: fullStars }).map((_, i) => (
        <span key={`f${i}`} className="text-yellow-400 text-xs leading-none">★</span>
      ))}
      {hasHalf && <HalfStar />}
      {Array.from({ length: emptyStars }).map((_, i) => (
        <span key={`e${i}`} className="text-yellow-400/30 text-xs leading-none">★</span>
      ))}
    </div>
  );
};

const BookingBadge = ({ name, targetScore, targetCount, label, reviewsLabel }) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true });
  const [score, setScore] = useState(0);
  const [count, setCount] = useState(0);

  useEffect(() => {
    if (!isInView) return;
    const duration = 3500;
    const startTime = Date.now();
    const tick = () => {
      const elapsed = Date.now() - startTime;
      const progress = Math.min(elapsed / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 2);
      setScore(targetScore * eased);
      setCount(Math.round(targetCount * eased));
      if (progress < 1) requestAnimationFrame(tick);
      else {
        setScore(targetScore);
        setCount(targetCount);
      }
    };
    requestAnimationFrame(tick);
  }, [isInView, targetScore, targetCount]);

  return (
    <div ref={ref} className="bg-lux-surface border border-lux-accent rounded-xl p-4 flex items-center gap-4 flex-1 min-w-[220px]">
      <span className="bg-white text-[#003580] font-black text-xl w-10 h-10 flex items-center justify-center rounded-lg shrink-0 leading-none">
        B.
      </span>
      <div>
        <p className="text-lux-cream text-sm font-semibold leading-tight">{name}</p>
        <div className="flex items-baseline gap-2 mt-0.5">
          <span className="text-lux-accent text-2xl font-medium tracking-wide leading-none">{score.toFixed(1)}</span>
          <span className="text-lux-cream/80 text-sm">{label}</span>
        </div>
        <p className="text-lux-cream/50 text-xs mt-0.5">
          {count} {reviewsLabel}
        </p>
      </div>
    </div>
  );
};

const Reviews = () => {
  const { t } = useTranslation();
  const width = useWindowWidth();
  const isMobile = width < 768;

  const itemsPerView = isMobile ? 1 : 2;
  const maxIndex = Math.max(0, reviews.length - itemsPerView);

  const [currentIndex, setCurrentIndex] = useState(0);
  const [visible, setVisible] = useState(true);

  useEffect(() => {
    setCurrentIndex((i) => Math.min(i, maxIndex));
  }, [maxIndex]);

  const navigate = (dir) => {
    setVisible(false);
    setTimeout(() => {
      setCurrentIndex((i) => {
        if (dir === "prev") return Math.max(0, i - 1);
        return Math.min(maxIndex, i + 1);
      });
      setVisible(true);
    }, 250);
  };

  const visibleReviews = reviews.slice(currentIndex, currentIndex + itemsPerView);
  const canPrev = currentIndex > 0;
  const canNext = currentIndex < maxIndex;

  return (
    <section
      id="reviews"
      className="min-h-[400px] p-10 relative mask anchor"
    >
      <div className="flex flex-col items-center justify-center">
        <h1 className="font-sans text-lux-highlight text-[12px] font-bold tracking-widest z-10">
          {t("reviews.subtitle")}
        </h1>
        <h1 className="text-white text-center text-[35px] font-medium tracking-wide z-10">
          {t("reviews.title")}
        </h1>

        <div className="flex flex-col sm:flex-row gap-4 mt-8 z-10 w-full max-w-[1200px] justify-center">
          <BookingBadge
            name="New Moon Rooms"
            targetScore={9.0}
            targetCount={761}
            label={t("reviews.excellent")}
            reviewsLabel={t("reviews.reviewsCount")}
          />
          <BookingBadge
            name="New Moon Del Prione"
            targetScore={8.9}
            targetCount={168}
            label={t("reviews.fabulous")}
            reviewsLabel={t("reviews.reviewsCount")}
          />
        </div>

        <p className="text-white/60 text-xs mt-3 z-10">{t("reviews.source")}</p>

        <div className="mt-8 w-full max-w-[1200px] z-10">
          <div
            className={`flex flex-col md:flex-row gap-6 transition-opacity duration-300 ${
              visible ? "opacity-100" : "opacity-0"
            }`}
          >
            {visibleReviews.map((review, index) => (
              <div
                className="bg-lux-surface text-lux-cream p-6 rounded-lg flex items-start shadow-md w-full h-[26rem] md:h-80 overflow-hidden"
                key={index}
              >
                <div className="flex flex-col items-center mr-6 shrink-0">
                  <div className="w-12 h-12 rounded-full bg-lux-accent flex items-center justify-center text-lux-bg font-bold text-lg">
                    {review.user.name[0]}
                  </div>
                  <div className="mt-2 flex flex-col items-center gap-1">
                    <span className="bg-[#003580] text-white text-[10px] font-bold px-1.5 py-0.5 rounded leading-none whitespace-nowrap">
                      B. {review.user.score}/10
                    </span>
                    <Stars score={review.user.score} />
                  </div>
                </div>
                <div className="border-l-2 border-lux-muted pl-6 flex-1 min-w-0 h-full overflow-y-auto">
                  <div className="mb-2">
                    <h3 className="text-lg font-semibold leading-tight">{review.user.name}</h3>
                    <p className="text-lux-accent/80 text-xs font-medium tracking-wide">{review.user.structure}</p>
                    <p className="text-lux-highlight text-sm mt-0.5">{review.user.date}</p>
                  </div>
                  <p className="italic text-sm leading-relaxed">{review.text}</p>
                </div>
              </div>
            ))}
          </div>

          <div className="flex justify-center gap-4 mt-6">
            <button
              onClick={() => navigate("prev")}
              disabled={!canPrev}
              className="w-10 h-10 rounded-full border border-lux-accent flex items-center justify-center text-lux-accent disabled:opacity-30 disabled:cursor-not-allowed hover:bg-lux-accent hover:text-lux-bg transition-colors"
              aria-label="Previous"
            >
              ←
            </button>
            <button
              onClick={() => navigate("next")}
              disabled={!canNext}
              className="w-10 h-10 rounded-full border border-lux-accent flex items-center justify-center text-lux-accent disabled:opacity-30 disabled:cursor-not-allowed hover:bg-lux-accent hover:text-lux-bg transition-colors"
              aria-label="Next"
            >
              →
            </button>
          </div>

          <div className="flex justify-center mt-8">
            <a
              href="/camere"
              className="inline-block bg-lux-accent-strong hover:bg-lux-highlight text-[#161310] font-semibold py-3 px-10 rounded-sm border-2 border-lux-accent hover:bg-lux-bg hover:text-lux-accent transition-all duration-300"
            >
              {t("reviews.ctaButton")}
            </a>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Reviews;
